import type { ApiError } from '@bedouin/shared'

/**
 * The single point where the front end talks to the Worker.
 *
 * `VITE_API_BASE_URL` is the only thing that differs between local and
 * production, so nothing else in the app needs to know where the API lives.
 */
export const API_BASE_URL: string = (
  import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:8787'
).replace(/\/$/, '')

/** Thrown for any non-2xx response, carrying the API's structured detail. */
export class ApiRequestError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly fields?: Record<string, string>,
  ) {
    super(message)
    this.name = 'ApiRequestError'
  }

  /** True when the failure is the user's input rather than a server fault. */
  get isValidation(): boolean {
    return this.status === 400 || this.status === 422
  }
}

export type QueryValue = string | number | boolean | null | undefined | string[]

function buildUrl(path: string, query?: Record<string, QueryValue>): string {
  const url = new URL(`${API_BASE_URL}${path}`)
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value === null || value === undefined || value === '') continue
      // Repeated params are how the API takes multi-value filters
      // (?status=a&status=b), so arrays expand rather than joining.
      if (Array.isArray(value)) {
        for (const item of value) url.searchParams.append(key, item)
      } else {
        url.searchParams.set(key, String(value))
      }
    }
  }
  return url.toString()
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'
  query?: Record<string, QueryValue>
  body?: unknown
  signal?: AbortSignal
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', query, body, signal } = options

  const isFormData = body instanceof FormData
  let response: Response

  try {
    response = await fetch(buildUrl(path, query), {
      method,
      // Cookies are sent so a future session-based auth needs no change here.
      credentials: 'include',
      // Let the browser set the multipart boundary itself.
      headers: isFormData || body === undefined ? {} : { 'Content-Type': 'application/json' },
      body: isFormData ? body : body === undefined ? undefined : JSON.stringify(body),
      signal,
    })
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err
    // fetch only rejects for network-level failures, which read very
    // differently to the user than an API error.
    throw new ApiRequestError(
      0,
      'network_error',
      `Could not reach the API at ${API_BASE_URL}. Is the Worker running?`,
    )
  }

  if (response.status === 204) return undefined as T

  const payload = (await response.json().catch(() => null)) as
    | { data: T }
    | ApiError
    | null

  if (!response.ok) {
    const detail = payload && 'error' in payload ? payload.error : null
    throw new ApiRequestError(
      response.status,
      detail?.code ?? 'unknown',
      detail?.message ?? `Request failed with status ${response.status}`,
      detail?.fields,
    )
  }

  if (!payload || !('data' in payload)) {
    throw new ApiRequestError(response.status, 'bad_response', 'API response was malformed')
  }
  return payload.data
}

export const api = {
  get: <T>(path: string, query?: Record<string, QueryValue>, signal?: AbortSignal) =>
    request<T>(path, { query, signal }),

  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body }),

  patch: <T>(path: string, body: unknown) => request<T>(path, { method: 'PATCH', body }),

  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),

  /** Multipart upload, used for documents and masters. */
  upload: <T>(path: string, form: FormData) => request<T>(path, { method: 'POST', body: form }),

  /** Absolute URL for a link the browser follows directly (downloads). */
  url: (path: string) => `${API_BASE_URL}${path}`,
}
